import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import AdminJobMatchService from '../services/AdminJobMatchService';
import ProjectJobMatching from '../types/jobMatch/ProjectJobMatching';
import ProjectAvailableJobMatching from '../types/jobMatch/ProjectAvailableJobMatching';
import UploadState from '../../types/UploadState';

const NAME = 'AdminJobMatch';

const getJobMatchings = createAsyncThunk(
    `${NAME}/getJobMatchings`,
    AdminJobMatchService.getJobMatchings,
);
const getAvailableJobMatchings = createAsyncThunk(
    `${NAME}/getAvailableJobMatchings`,
    AdminJobMatchService.getAvailableJobMatchings,
);
const assignProject = createAsyncThunk(
    `${NAME}/assignProject`,
    AdminJobMatchService.assignProject,
);

export type AdminJobMatchState = {
    jobMatchings: UploadState<ProjectJobMatching[]>;
    availableJobMatchings: IBaseState<ProjectAvailableJobMatching[]>;
};

const initialState: AdminJobMatchState = {
    jobMatchings: {
        data: [],
        loading: false,
        uploading: false,
    },
    availableJobMatchings: {
        data: [],
        loading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getJobMatchings.pending, (state) => {
            state.jobMatchings.loading = true;
        });
        builder.addCase(getJobMatchings.fulfilled, (state, action) => {
            state.jobMatchings.data = action.payload.data;
        });
        builder.addCase(getJobMatchings.rejected, (state) => {
            state.jobMatchings.data = [];
        });
        builder.addCase(assignProject.pending, (state) => {
            state.jobMatchings.uploading = true;
        });

        builder.addCase(getAvailableJobMatchings.pending, (state) => {
            state.availableJobMatchings.loading = true;
        });
        builder.addCase(getAvailableJobMatchings.fulfilled, (state, action) => {
            state.availableJobMatchings.data = action.payload.data;
        });
        builder.addCase(getAvailableJobMatchings.rejected, (state) => {
            state.availableJobMatchings.data = [];
        });

        builder.addMatcher(
            isAnyOf(getJobMatchings.fulfilled, getJobMatchings.rejected),
            (state) => {
                state.jobMatchings.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(assignProject.fulfilled, assignProject.rejected),
            (state) => {
                state.jobMatchings.uploading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getAvailableJobMatchings.fulfilled,
                getAvailableJobMatchings.rejected,
            ),
            (state) => {
                state.availableJobMatchings.loading = false;
            },
        );
    },
});

const state = (state: AppState): AdminJobMatchState => state.admin.jobMatch;

const AdminJobMatchSlice = {
    reducer: slice.reducer,
    actions: {
        getJobMatchings,
        getAvailableJobMatchings,
        assignProject,
    },
    state: {
        all: createSelector(state, (state: AdminJobMatchState) => state),
        jobMatchings: createSelector(
            state,
            (state: AdminJobMatchState) => state.jobMatchings,
        ),
        availableJobMatchings: createSelector(
            state,
            (state: AdminJobMatchState) => state.availableJobMatchings,
        ),
    },
};

export default AdminJobMatchSlice;
